import React from 'react';
import { useSelector } from 'react-redux';

import { PetsAttribute } from '@gumtree/shared/src/types/attributes';

import { VipState } from '../../reducers';
import {
    AttributeCell,
    AttributesContainer,
    AttributeText,
    Container,
    SecondaryTitle,
    Title,
} from './index.style';
import DogBreedIcon from './icons/breed-dog';
import CatBreedIcon from './icons/breed-cat';
import AgeIcon from './icons/age';
import ReadyToLeaveIcon from './icons/ready-to-leave';
import TickIcon from './icons/tick';
import CrossIcon from './icons/cross';

const attributeConfig: {
    key: string;
    icon: React.ComponentType;
    formatValue: (value: string) => string;
}[] = [
    {
        key: 'dog_breed',
        icon: DogBreedIcon,
        formatValue: (value: string) => value,
    },
    {
        key: 'cat_breed',
        icon: CatBreedIcon,
        formatValue: (value: string) => value,
    },
    {
        key: 'pet_date_of_birth',
        icon: AgeIcon,
        formatValue: (value: string) => `Age: ${value}`,
    },
    {
        key: 'pet_rehome_ready_date',
        icon: ReadyToLeaveIcon,
        formatValue: (value: string) => `Ready to leave: ${value}`,
    },
];

const healthInformation: { key: PetsAttribute['key']; description: string }[] = [
    { key: 'deflead', description: 'Deflead' },
    { key: 'microchipped', description: 'Microchipped' },
    { key: 'viewable_with_mother', description: 'Viewable with mother' },
    { key: 'neutered_or_spayed', description: 'Neutered / Spayed' },
    { key: 'vaccinated', description: 'Vaccinated' },
];

const PetsAttributes = ({ isMobileCardsLayout = false }: { isMobileCardsLayout?: boolean }) => {
    const { attributes } = useSelector(({ adDetails: { attributes } }: VipState) => ({
        attributes: attributes as PetsAttribute[],
    }));

    const getAttribute = (key: PetsAttribute['key']) =>
        attributes?.find((attr) => attr.key === key);

    const isHealthDocumentationShown = healthInformation.some(({ key }) =>
        Boolean(getAttribute(key))
    );

    return (
        <Container isMobileCardsLayout={isMobileCardsLayout}>
            <Title>Details</Title>
            <AttributesContainer>
                {attributeConfig.map(({ key, icon: Icon, formatValue }) => {
                    const attribute = getAttribute(key as PetsAttribute['key']);
                    if (!attribute) return null;
                    return (
                        <AttributeCell key={key}>
                            <Icon />
                            <AttributeText>{formatValue(attribute.value)}</AttributeText>
                        </AttributeCell>
                    );
                })}
            </AttributesContainer>
            {isHealthDocumentationShown && (
                <>
                    <SecondaryTitle>Health and documentation</SecondaryTitle>
                    <AttributesContainer>
                        {healthInformation.map(({ key, description }) => {
                            const attribute = getAttribute(key);
                            if (!attribute) return null;
                            return (
                                <AttributeCell key={key}>
                                    {attribute.value === 'Yes' ? <TickIcon /> : <CrossIcon />}
                                    <AttributeText>{description}</AttributeText>
                                </AttributeCell>
                            );
                        })}
                    </AttributesContainer>
                </>
            )}
        </Container>
    );
};

export default PetsAttributes;
